import Image from 'next/image';
import type { ComponentType, SVGProps } from 'react';
import { Globe, Mail, ShoppingCart } from 'lucide-react';
import { getIntegrationBrand } from '@/lib/brand/integration-brands';
import type { IntegrationPlatform } from '@/lib/types/integrations';
import { cn } from '@/lib/utils';
import { Ebay } from './svgs/ebay';
import { Shopify } from './svgs/shopify';
import { Stripe } from './svgs/stripe';
import { Paypal } from './svgs/paypal';
import { XeroBrand } from './svgs/xero';
import { ZapierBrand } from './svgs/zapier';
import { WiseBrand } from './svgs/wise';
import { Veeqo } from './svgs/veeqo';
import { ShipStation } from './svgs/shipstation';
import { Square } from './svgs/square';

interface PlatformLogoProps {
  platform: string;
  size?: number;
  className?: string;
  fallbackSize?: number;
  /** An empty label makes the mark decorative beside a visible source name. */
  alt?: string;
}

const LOGOS: Partial<
  Record<IntegrationPlatform, ComponentType<SVGProps<SVGSVGElement>>>
> = {
  ebay: Ebay,
  shopify: Shopify,
  stripe: Stripe,
  paypal: Paypal,
  xero: XeroBrand,
  zapier: ZapierBrand,
  veeqo: Veeqo,
  shipstation: ShipStation,
  square: Square,
};

/** Shared by product and marketing. Never recolor or fabricate a vendor mark. */
export function PlatformLogo({
  platform,
  size = 20,
  className,
  fallbackSize = size,
  alt,
}: PlatformLogoProps) {
  if (!platform) return null;
  const brand = getIntegrationBrand(platform);
  const classNames = cn('inline-block shrink-0', className);
  const label = alt ?? `${brand?.name ?? platform} logo`;
  const width = size * (brand?.logoAspectRatio ?? 1);

  if (brand?.logoPath) {
    return (
      <Image
        src={brand.logoPath}
        alt={label}
        width={Math.round(width)}
        height={size}
        style={{ width, height: size, maxWidth: '100%', objectFit: 'contain' }}
        className={classNames}
        unoptimized
      />
    );
  }

  const Logo = brand
    ? LOGOS[brand.id]
    : platform.trim().toLowerCase() === 'wise'
      ? WiseBrand
      : undefined;
  if (Logo) {
    return (
      <Logo
        role={label ? 'img' : undefined}
        aria-label={label || undefined}
        aria-hidden={!label || undefined}
        className={classNames}
        style={{ width, height: size, maxWidth: '100%' }}
      />
    );
  }

  const Icon =
    brand?.id === 'email'
      ? Mail
      : platform === 'ecommerce'
        ? ShoppingCart
        : Globe;
  const iconLabel = alt ?? `${brand?.name ?? platform} icon`;
  return (
    <Icon
      role={iconLabel ? 'img' : undefined}
      aria-label={iconLabel || undefined}
      aria-hidden={!iconLabel || undefined}
      size={fallbackSize}
      className={classNames}
    />
  );
}
